import React, { KeyboardEvent, useCallback, useMemo } from 'react';
import Icon from '../../../icons/Icon';
import EditableFieldRow from '../../custom/EditableFieldRow';
import ExpandableGroup, { ExpandableGroupHeaderContext, useAccordionState } from '../../custom/ExpandableGroup';
import { AccordionIndicator, AccordionOpenCloseIcon } from '../../custom/AccordionIndicators';
import SarAccordionSectionHeader from './SarAccordionSectionHeader';
import SarValidationCategoryMapper from '../../../util/SarValidationCategoryMapper';
import { SarContactInfo, SarDigerKisi, SarDigerKisiAddress, SarValidationResult } from '../SarInterfaces';
import { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import { getContactInfoTypeOptions, SarXmlTags } from '../../../constant/SarTagConstants';
import SarParser from '../SarParser';
import ADDRESS_TAGS = SarXmlTags.ADDRESS_TAGS;
import DIGERKISI_TAGS = SarXmlTags.DIGERKISI_TAGS;

export type { SarDigerKisiAddress, SarDigerKisi };

const buildInfoConfig = (path: string, type: string = 'Metin') => ({
  path,
  type,
  mode: 'bubble' as const,
});

const DigerKisilerAccordion = ({ xmlContent, mutateXmlContent, validate }: Props) => {
  const invalidFieldIds = useMemo<Set<string>>(() => {
    if (!validate || !validate.errors || validate.errors.length === 0) {
      return new Set<string>();
    }
    return SarValidationCategoryMapper.getInvalidFieldIds(validate.errors);
  }, [validate]);
  const digerKisiler = useMemo(() => SarParser.parseDigerKisiler(xmlContent), [xmlContent]);
  const accordion = useAccordionState();

  const errorsArray = validate?.errors ?? [];
  const errorsKey = errorsArray.map((e) => `${e.lineStart}-${e.lineEnd}-${e.message}`).join('|');
  const personErrors = useMemo<Map<number, boolean>>(() => {
    const errors = new Map<number, boolean>();
    const currentInvalidFieldIds = errorsArray.length > 0 ? SarValidationCategoryMapper.getInvalidFieldIds(errorsArray) : new Set<string>();

    digerKisiler.forEach((_, index) => {
      const personPrefix = `digerkisi-${index}-`;
      const hasError = Array.from(currentInvalidFieldIds).some((fieldId) => {
        return fieldId.startsWith(personPrefix);
      });
      errors.set(index, hasError);
    });
    return errors;
  }, [digerKisiler.length, errorsKey]);

  const handleFieldChange = useCallback(
    (personIndex: number, tagName: string, value: string) => {
      mutateXmlContent((document) => {
        const person = document.getElementsByTagName('digerkisi').item(personIndex);
        if (!person) return;
        let target = person.getElementsByTagName(tagName).item(0);
        if (!target) {
          target = document.createElement(tagName);
          person.appendChild(target);
        }
        target.textContent = value;
      });
    },
    [mutateXmlContent],
  );

  const handleAddressFieldChange = useCallback(
    (personIndex: number, addressIndex: number, tagName: string, value: string) => {
      mutateXmlContent((document) => {
        const person = document.getElementsByTagName('digerkisi').item(personIndex);
        if (!person) return;
        let addressesParent = person.getElementsByTagName('adresler').item(0);
        if (!addressesParent) {
          addressesParent = document.createElement('adresler');
          person.appendChild(addressesParent);
        }
        const addressNode = addressesParent.getElementsByTagName('adres').item(addressIndex);
        if (!addressNode) return;
        if (tagName === 'tip') {
          addressNode.setAttribute('tip', value);
          return;
        }
        let target = addressNode.getElementsByTagName(tagName).item(0);
        if (!target) {
          target = document.createElement(tagName);
          addressNode.appendChild(target);
        }
        target.textContent = value;
      });
    },
    [mutateXmlContent],
  );

  const getOrCreateParent = ({ document, parentElement, tagName }: GetOrCreateParentParams): Element => {
    let parent = parentElement.getElementsByTagName(tagName).item(0);
    if (!parent) {
      parent = document.createElement(tagName);
      parentElement.appendChild(parent);
    }
    return parent;
  };

  const handleContactInfoTipChange = useCallback(
    (personIndex: number, contactIndex: number, tip: string): void => {
      mutateXmlContent((document) => {
        const person = document.getElementsByTagName('digerkisi').item(personIndex);
        if (!person) return;
        const contactsParent = getOrCreateParent({
          document,
          parentElement: person,
          tagName: 'iletisimbilgileri',
        });
        const contactNode = contactsParent.getElementsByTagName('iletisimbilgisi').item(contactIndex);
        if (!contactNode) return;
        contactNode.setAttribute('tip', tip);
      });
    },
    [mutateXmlContent],
  );

  const handleContactInfoValueChange = useCallback(
    (personIndex: number, contactIndex: number, value: string) => {
      mutateXmlContent((document) => {
        const person = document.getElementsByTagName('digerkisi').item(personIndex);
        if (!person) return;
        const contactsParent = getOrCreateParent({
          document,
          parentElement: person,
          tagName: 'iletisimbilgileri',
        });
        const contactNode = contactsParent.getElementsByTagName('iletisimbilgisi').item(contactIndex);
        if (!contactNode) return;
        contactNode.textContent = value;
      });
    },
    [mutateXmlContent],
  );

  const handleAddDigerKisi = () => {
    mutateXmlContent((document) => {
      let parent = document.getElementsByTagName('digerkisiler').item(0);
      if (!parent) {
        parent = document.createElement('digerkisiler');
        document.documentElement.appendChild(parent);
      }
      const digerKisi = document.createElement('digerkisi');
      DIGERKISI_TAGS.forEach((tag) => {
        const element = document.createElement(tag);
        element.textContent = '';
        digerKisi.appendChild(element);
      });
      digerKisi.appendChild(document.createElement('adresler'));
      digerKisi.appendChild(document.createElement('iletisimbilgileri'));
      parent.appendChild(digerKisi);
    });
  };

  const handleAddAddress = (personIndex: number) => {
    mutateXmlContent((document) => {
      const person = document.getElementsByTagName('digerkisi').item(personIndex);
      if (!person) return;
      let addressesParent = person.getElementsByTagName('adresler').item(0);
      if (!addressesParent) {
        addressesParent = document.createElement('adresler');
        person.appendChild(addressesParent);
      }
      const addressNode = document.createElement('adres');
      addressNode.setAttribute('tip', '');
      ADDRESS_TAGS.forEach((tag) => {
        const element = document.createElement(tag);
        element.textContent = '';
        addressNode.appendChild(element);
      });
      addressesParent.appendChild(addressNode);
    });
  };

  const handleAddContact = useCallback(
    (personIndex: number): void => {
      mutateXmlContent((document) => {
        const person = document.getElementsByTagName('digerkisi').item(personIndex);
        if (!person) return;
        const contactsParent = getOrCreateParent({
          document,
          parentElement: person,
          tagName: 'iletisimbilgileri',
        });
        const contactNode = document.createElement('iletisimbilgisi');
        contactNode.setAttribute('tip', '');
        contactNode.textContent = '';
        contactsParent.appendChild(contactNode);
      });
    },
    [mutateXmlContent],
  );

  const createGroupHeaderRenderer =
    (fieldPath: string) =>
    ({ label, hasError }: ExpandableGroupHeaderContext): JSX.Element => (
      <SarAccordionSectionHeader
        label={label}
        fieldPath={fieldPath}
        infoLabel='Information'
        triggerIcon='kyc_informationicon'
        triggerClassName='text-[#98A2B3]'
        hasError={hasError}
      />
    );

  const createIletisimBilgileriHeaderRenderer =
    (fieldPath: string) =>
    ({ label, hasError }: ExpandableGroupHeaderContext): JSX.Element => {
      return (
        <SarAccordionSectionHeader
          label={label}
          fieldPath={fieldPath}
          infoLabel='Information'
          triggerIcon='kyc_informationicon'
          triggerClassName='text-[#98A2B3]'
          hasError={hasError}
        />
      );
    };

  const content = (
    <div className={'h-full w-full pr-6 pt-3 2xl:pt-4 flex flex-col gap-y-3'}>
      <div className={'flex items-center justify-between'}>
        <div className={'text-[#1D2939] text-base font-semibold'}>XML Document</div>
        <button
          onClick={handleAddDigerKisi}
          type='button'
          className='w-5 h-5 hover:bg-f-gray-2 rounded-sm flex items-center justify-center transition-all duration-200'
        >
          <Icon icon='add' className='text-[#1D2939]' />
        </button>
      </div>
      {digerKisiler.length === 0 ? (
        <div className={'rounded-xl border border-dashed border-[#D0D5DD] bg-[#F9FAFB] px-6 py-10 text-center text-sm text-[#667085]'}>
          Henüz diğer kişi bulunmuyor.
        </div>
      ) : (
        <div className={'flex flex-col border rounded-[10px] h-full overflow-y-auto overflow-hidden'}>
          {digerKisiler.map((person, index) => {
            const personKey = `diger-kisi-${index}`;
            const expanded = accordion.isExpanded(personKey);
            const hasPersonError = personErrors.get(index) ?? false;
            return (
              <div key={person.id} className={'bg-white rounded-t-xl rounded-b-xl'}>
                <div
                  role={'button'}
                  tabIndex={0}
                  className={`flex w-full rounded-t-xl items-center justify-between px-5 2xl:px-6 h-[56px] 2xl:h-[58px] text-left border-b ${index === digerKisiler.length - 1 ? '' : 'border-b'}`}
                  onClick={() => accordion.toggle(personKey)}
                  onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      accordion.toggle(personKey);
                    }
                  }}
                >
                  <div className='flex items-center gap-x-3'>
                    <div>
                      <AccordionOpenCloseIcon expanded={expanded} hasError={hasPersonError} />
                    </div>
                    <SarAccordionSectionHeader
                      label={`Diğer Kişi ${index + 1}`}
                      fieldPath={'digerKisi'}
                      infoLabel='Information'
                      hasError={hasPersonError}
                      triggerIcon='kyc_informationicon'
                      triggerClassName={hasPersonError ? 'text-f-red-1' : 'text-[#667085]'}
                    />
                  </div>
                  <AccordionIndicator expanded={expanded} />
                </div>
                {expanded && (
                  <div className={'flex flex-col divide-y divide-[#F2F4F7] border-b'}>
                    <EditableFieldRow
                      label={'İşlemle Bağlantısı'}
                      value={person.connectionId}
                      onChange={(value) => handleFieldChange(index, 'islemlebaglantisi', value)}
                      hasError={invalidFieldIds.has(`digerkisi-${index}-islemlebaglantisi`)}
                      infoConfig={buildInfoConfig('digerKisi.islemleBaglantisi', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'İşlemle Bağlantısı Açıklama'}
                      value={person.connectionDescription}
                      onChange={(value) => handleFieldChange(index, 'islemlebaglantisiaciklama', value)}
                      hasError={invalidFieldIds.has(`digerkisi-${index}-islemlebaglantisiaciklama`)}
                      infoConfig={buildInfoConfig('digerKisi.islemleBaglantisiAciklama')}
                    />
                    <EditableFieldRow
                      label={'Kuruluş Nevi'}
                      value={person.establishmentType}
                      onChange={(value) => handleFieldChange(index, 'kurulusnevi', value)}
                      hasError={invalidFieldIds.has(`digerkisi-${index}-kurulusnevi`)}
                      infoConfig={buildInfoConfig('digerKisi.kurulusNevi', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'Unvan'}
                      value={person.title}
                      onChange={(value) => handleFieldChange(index, 'unvan', value)}
                      hasError={invalidFieldIds.has(`digerkisi-${index}-unvan`)}
                      infoConfig={buildInfoConfig('digerKisi.unvan')}
                    />
                    <EditableFieldRow
                      label={'İşletme Adı'}
                      value={person.businessName}
                      onChange={(value) => handleFieldChange(index, 'isletmeadi', value)}
                      hasError={invalidFieldIds.has(`digerkisi-${index}-isletmeadi`)}
                      infoConfig={buildInfoConfig('digerKisi.isletmeAdi')}
                    />
                    <EditableFieldRow
                      label={'VKN'}
                      value={person.taxId}
                      onChange={(value) => handleFieldChange(index, 'vkn', value)}
                      hasError={invalidFieldIds.has(`digerkisi-${index}-vkn`)}
                      infoConfig={buildInfoConfig('digerKisi.vkn', 'Kimlik')}
                    />
                    <EditableFieldRow
                      label={'Sicil No'}
                      value={person.registryNo}
                      onChange={(value) => handleFieldChange(index, 'sicilno', value)}
                      hasError={invalidFieldIds.has(`digerkisi-${index}-sicilno`)}
                      infoConfig={buildInfoConfig('digerKisi.sicilNo')}
                    />
                    <EditableFieldRow
                      label={'Faaliyet Konusu'}
                      value={person.activitySubject}
                      onChange={(value) => handleFieldChange(index, 'faaliyetkonusu', value)}
                      hasError={invalidFieldIds.has(`digerkisi-${index}-faaliyetkonusu`)}
                      infoConfig={buildInfoConfig('digerKisi.faaliyetKonusu')}
                    />
                    <EditableFieldRow
                      label={'Ülke Kodu'}
                      value={person.countryCode}
                      onChange={(value) => handleFieldChange(index, 'ulkekodu', value)}
                      hasError={invalidFieldIds.has(`digerkisi-${index}-ulkekodu`)}
                      infoConfig={buildInfoConfig('digerKisi.ulkeKodu', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'İl Kodu'}
                      value={person.cityCode}
                      onChange={(value) => handleFieldChange(index, 'ilkodu', value)}
                      hasError={invalidFieldIds.has(`digerkisi-${index}-ilkodu`)}
                      infoConfig={buildInfoConfig('digerKisi.ilKodu', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'İlçe Kodu'}
                      value={person.districtCode}
                      onChange={(value) => handleFieldChange(index, 'ilcekodu', value)}
                      hasError={invalidFieldIds.has(`digerkisi-${index}-ilcekodu`)}
                      infoConfig={buildInfoConfig('digerKisi.ilceKodu', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'Kuruluş Tarihi'}
                      value={person.establishmentDate}
                      onChange={(value) => handleFieldChange(index, 'kurulustarihi', value)}
                      hasError={invalidFieldIds.has(`digerkisi-${index}-kurulustarihi`)}
                      infoConfig={buildInfoConfig('digerKisi.kurulusTarihi', 'Tarih')}
                    />
                    <EditableFieldRow
                      label={'Web Sitesi'}
                      value={person.website}
                      onChange={(value) => handleFieldChange(index, 'websitesi', value)}
                      hasError={invalidFieldIds.has(`digerkisi-${index}-websitesi`)}
                      infoConfig={buildInfoConfig('digerKisi.websitesi')}
                    />
                    <EditableFieldRow
                      label={'Ek Bilgi'}
                      value={person.additionalInfo}
                      multiline
                      onChange={(value) => handleFieldChange(index, 'ekbilgi', value)}
                      hasError={invalidFieldIds.has(`digerkisi-${index}-ekbilgi`)}
                      infoConfig={buildInfoConfig('digerKisi.ekBilgi')}
                    />
                    <ExpandableGroup<SarDigerKisiAddress>
                      baseKey={`${personKey}-addresses`}
                      label={'Adresler'}
                      items={person.addresses}
                      accordion={accordion}
                      getItemTitle={(item, addressIndex) => `Adres ${addressIndex + 1}`}
                      onAdd={() => handleAddAddress(index)}
                      renderHeaderDetails={createGroupHeaderRenderer('digerKisi.adresler')}
                      hasError={
                        invalidFieldIds.has(`digerkisi-${index}-adresler`) ||
                        Array.from(invalidFieldIds).some((fieldId) => fieldId.startsWith(`digerkisi-${index}-adres-`))
                      }
                      getItemHasError={(address, addressIndex) => {
                        return Array.from(invalidFieldIds).some((fieldId) => fieldId.startsWith(`digerkisi-${index}-adres-${addressIndex}-`));
                      }}
                      renderItems={(address, addressIndex) => (
                        <div className={'flex flex-col divide-y divide-[#F2F4F7] rounded-b-xl'}>
                          <EditableFieldRow
                            className='pl-20 2xl:pl-28'
                            label={'Adres Tipi'}
                            value={address.type}
                            onChange={(value) => handleAddressFieldChange(index, addressIndex, 'tip', value)}
                            hasError={invalidFieldIds.has(`digerkisi-${index}-adres-${addressIndex}-tip`)}
                            infoConfig={buildInfoConfig('digerkisi.adres', 'Kod')}
                          />
                          <EditableFieldRow
                            className='pl-20 2xl:pl-28'
                            label={'Adres Satırı'}
                            value={address.line}
                            onChange={(value) => handleAddressFieldChange(index, addressIndex, 'adressatir', value)}
                            hasError={invalidFieldIds.has(`digerkisi-${index}-adres-${addressIndex}-adressatir`)}
                            infoConfig={buildInfoConfig('digerkisi.adres.adresSatir')}
                          />
                          <EditableFieldRow
                            className='pl-20 2xl:pl-28'
                            label={'Posta Kodu'}
                            value={address.postalCode}
                            onChange={(value) => handleAddressFieldChange(index, addressIndex, 'postakodu', value)}
                            hasError={invalidFieldIds.has(`digerkisi-${index}-adres-${addressIndex}-postakodu`)}
                            infoConfig={buildInfoConfig('digerkisi.adres.postaKodu')}
                          />
                          <EditableFieldRow
                            className='pl-20 2xl:pl-28'
                            label={'İl Kodu'}
                            value={address.cityCode}
                            onChange={(value) => handleAddressFieldChange(index, addressIndex, 'ilkodu', value)}
                            hasError={invalidFieldIds.has(`digerkisi-${index}-adres-${addressIndex}-ilkodu`)}
                            infoConfig={buildInfoConfig('digerkisi.adres.ilKodu', 'Kod')}
                          />
                          <EditableFieldRow
                            className='pl-20 2xl:pl-28'
                            label={'İlçe Kodu'}
                            value={address.districtCode}
                            onChange={(value) => handleAddressFieldChange(index, addressIndex, 'ilcekodu', value)}
                            hasError={invalidFieldIds.has(`digerkisi-${index}-adres-${addressIndex}-ilcekodu`)}
                            infoConfig={buildInfoConfig('digerkisi.adres.ilceKodu', 'Kod')}
                          />
                          <EditableFieldRow
                            className='pl-20 2xl:pl-28'
                            label={'Ülke Kodu'}
                            value={address.countryCode}
                            onChange={(value) => handleAddressFieldChange(index, addressIndex, 'ulkekodu', value)}
                            hasError={invalidFieldIds.has(`digerkisi-${index}-adres-${addressIndex}-ulkekodu`)}
                            infoConfig={buildInfoConfig('digerkisi.adres.ulkeKodu', 'Kod')}
                          />
                        </div>
                      )}
                      emptyLabel={'Adres bilgisi bulunamadı.'}
                    />
                    <ExpandableGroup<SarContactInfo>
                      baseKey={`${personKey}-contact`}
                      label={'İletişim Bilgileri'}
                      items={person.contactInfos}
                      accordion={accordion}
                      getItemTitle={(item, contactIndex) => `İletişim Bilgisi ${contactIndex + 1}`}
                      onAdd={() => handleAddContact(index)}
                      labelClassname={hasPersonError ? 'text-f-red-1' : ''}
                      renderHeaderDetails={createGroupHeaderRenderer('digerKisi.iletisimBilgileri')}
                      hasError={
                        invalidFieldIds.has(`digerkisi-${index}-iletisimbilgileri`) ||
                        Array.from(invalidFieldIds).some((fieldId) => fieldId.startsWith(`digerkisi-${index}-iletisimbilgisi`))
                      }
                      getItemHasError={(contact, contactIndex) => {
                        return (
                          invalidFieldIds.has(`digerkisi-${index}-iletisimbilgisi-${contactIndex}`) ||
                          invalidFieldIds.has(`digerkisi-${index}-iletisimbilgisi-${contactIndex}-tip`)
                        );
                      }}
                      renderItems={(contact, contactIndex) => (
                        <div className={'flex flex-col divide-y divide-[#F2F4F7] rounded-b-xl pl-12'}>
                          <EditableFieldRow
                            label={'Tip'}
                            value={contact.tip}
                            onChange={(value) => handleContactInfoTipChange(index, contactIndex, value)}
                            hasError={invalidFieldIds.has(`digerkisi-${index}-iletisimbilgisi-${contactIndex}-tip`)}
                            infoConfig={buildInfoConfig('digerKisi.iletisimBilgileri.tip', 'Kod')}
                            customOptions={getContactInfoTypeOptions() as FormicaSelectBoxItem[]}
                          />
                          <EditableFieldRow
                            label={'İletişim Bilgisi'}
                            value={contact.value}
                            onChange={(value) => handleContactInfoValueChange(index, contactIndex, value)}
                            hasError={invalidFieldIds.has(`digerkisi-${index}-iletisimbilgisi-${contactIndex}`)}
                          />
                        </div>
                      )}
                      emptyLabel={'İletişim bilgisi bulunamadı.'}
                    />
                    <EditableFieldRow
                      label={'Kimlik Bilgileri Sahte'}
                      value={person.fakeIdentityFlag}
                      onChange={(value) => handleFieldChange(index, 'kimlikbilgilerisahte', value)}
                      hasError={invalidFieldIds.has(`digerkisi-${index}-kimlikbilgilerisahte`)}
                      infoConfig={buildInfoConfig('digerKisi.kimlikBilgileriSahte', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'Mağdur'}
                      value={person.victimFlag}
                      onChange={(value) => handleFieldChange(index, 'magdur', value)}
                      hasError={invalidFieldIds.has(`digerkisi-${index}-magdur`)}
                      infoConfig={buildInfoConfig('digerKisi.magdur', 'Kod')}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return content;
};

export default DigerKisilerAccordion;

interface GetOrCreateParentParams {
  document: Document;
  parentElement: Element;
  tagName: string;
}

interface Props {
  xmlContent?: string;
  mutateXmlContent: (mutator: (document: Document) => void) => void;
  validate?: SarValidationResult;
}
